import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { Nullable, UserWithToken } from '@ygoargentina/sdk/api/api.types';
import axiosClient from './request-client';
import type { StreamFeaturedCard } from './streaming';

// =============================================================================
// Types
// =============================================================================

export type StreamControlScene = {
  slug: string;
  name: string;
};

/** Override de un elemento de la escena; vacío = sin override. */
export type StreamControlElement = {
  text?: string | null;
  imageUrl?: string | null;
};

/** Lo que se pisa de un jugador. */
export type StreamControlPlayerField = 'name' | 'score';
export type StreamControlPlayer = Partial<Record<StreamControlPlayerField, string>>;

export type StreamControlTarget = {
  /** null = OBS principal */
  sessionId: number | null;
  label: string;
  /** Para la URL del overlay */
  organizerSlug: string;
  sceneOrganizerSlug: string;
  sceneSlug: string;
  /** OBS: este torneo es el que está al aire. Sesión: siempre true. */
  onAir: boolean;
  scenes: StreamControlScene[];
  forcedSceneSlug: string | null;
  /** Campos pisados por clave (JSON). */
  fields: Record<string, string>;
  /**
   * Lo pisado por jugador (nombre y récord) por userId (JSON), válido sólo
   * para la ronda `round`: el API descarta lo de una ronda anterior.
   */
  playerNames: { round: string; byUserId: Record<string, StreamControlPlayer> } | null;
  /** Elementos pisados por clave (JSON). */
  elements: Record<string, StreamControlElement>;
  /** La carta del elemento «carta» (JSON); null = ninguna. */
  featuredCard: StreamFeaturedCard | null;
};

export type StreamControl = {
  tournamentId: number;
  title: string;
  targets: StreamControlTarget[];
};

/** Una carta que la detección cree ver en la mesa al aire. */
export type StreamCardSuggestion = {
  konamiId: number;
  name: string;
  imageUrl: string;
  /** 0..1 */
  confidence: number;
  firstSeenAt: string;
  lastSeenAt: string;
};

export type StreamCardSuggestions = {
  sessionId: number;
  matchId: number;
  updatedAt: string;
  /** La vista más recientemente primero. */
  cards: StreamCardSuggestion[];
};

export type StreamControlTargetArgs = {
  tournamentId: number;
  /** null/omitido = OBS principal */
  sessionId?: number | null;
};

// =============================================================================
// Queries / mutations
// =============================================================================

const TARGET_FIELDS = gql`
  fragment StreamControlTargetFields on StreamControlTarget {
    sessionId
    label
    organizerSlug
    sceneOrganizerSlug
    sceneSlug
    onAir
    scenes {
      slug
      name
    }
    forcedSceneSlug
    fields
    playerNames
    elements
    featuredCard
  }
`;

export const getStreamControl = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{ data: StreamControl }>(
    gql`
      query streamControl($tournamentId: Int!) {
        data: streamControl(tournamentId: $tournamentId) {
          tournamentId
          title
          targets {
            ...StreamControlTargetFields
          }
        }
      }
      ${TARGET_FIELDS}
    `,
    { tournamentId }
  );

export const setStreamForcedScene = async (
  user: Nullable<UserWithToken>,
  args: StreamControlTargetArgs & { sceneSlug?: string | null }
) =>
  getGraphQLQueryClient(user).request<{ data: StreamControlTarget }>(
    gql`
      mutation setStreamForcedScene($tournamentId: Int!, $sessionId: Int, $sceneSlug: String) {
        data: setStreamForcedScene(
          tournamentId: $tournamentId
          sessionId: $sessionId
          sceneSlug: $sceneSlug
        ) {
          ...StreamControlTargetFields
        }
      }
      ${TARGET_FIELDS}
    `,
    args
  );

export const setStreamFieldOverride = async (
  user: Nullable<UserWithToken>,
  args: StreamControlTargetArgs & { field: string; value?: string | null }
) =>
  getGraphQLQueryClient(user).request<{ data: StreamControlTarget }>(
    gql`
      mutation setStreamFieldOverride(
        $tournamentId: Int!
        $sessionId: Int
        $field: String!
        $value: String
      ) {
        data: setStreamFieldOverride(
          tournamentId: $tournamentId
          sessionId: $sessionId
          field: $field
          value: $value
        ) {
          ...StreamControlTargetFields
        }
      }
      ${TARGET_FIELDS}
    `,
    args
  );

export const setStreamPlayerName = async (
  user: Nullable<UserWithToken>,
  args: StreamControlTargetArgs & {
    userId: number;
    /** Sin `field`: el nombre. */
    name?: string | null;
    /** Con `field`, el valor va en `value`. */
    field?: StreamControlPlayerField;
    value?: string | null;
  }
) =>
  getGraphQLQueryClient(user).request<{ data: StreamControlTarget }>(
    gql`
      mutation setStreamPlayerName(
        $tournamentId: Int!
        $sessionId: Int
        $userId: Int!
        $name: String
        $field: String
        $value: String
      ) {
        data: setStreamPlayerName(
          tournamentId: $tournamentId
          sessionId: $sessionId
          userId: $userId
          name: $name
          field: $field
          value: $value
        ) {
          ...StreamControlTargetFields
        }
      }
      ${TARGET_FIELDS}
    `,
    args
  );

export const setStreamElementOverride = async (
  user: Nullable<UserWithToken>,
  args: StreamControlTargetArgs & {
    key: string;
    text?: string | null;
    imageUrl?: string | null;
  }
) =>
  getGraphQLQueryClient(user).request<{ data: StreamControlTarget }>(
    gql`
      mutation setStreamElementOverride(
        $tournamentId: Int!
        $sessionId: Int
        $key: String!
        $text: String
        $imageUrl: String
      ) {
        data: setStreamElementOverride(
          tournamentId: $tournamentId
          sessionId: $sessionId
          key: $key
          text: $text
          imageUrl: $imageUrl
        ) {
          ...StreamControlTargetFields
        }
      }
      ${TARGET_FIELDS}
    `,
    args
  );

/** Muestra una carta en el elemento «carta»; `konamiId` null la saca. */
export const setStreamFeaturedCard = async (
  user: Nullable<UserWithToken>,
  args: StreamControlTargetArgs & { konamiId: number | null }
) =>
  getGraphQLQueryClient(user).request<{ data: StreamControlTarget }>(
    gql`
      mutation setStreamFeaturedCard($tournamentId: Int!, $sessionId: Int, $konamiId: Int) {
        data: setStreamFeaturedCard(tournamentId: $tournamentId, sessionId: $sessionId, konamiId: $konamiId) {
          ...StreamControlTargetFields
        }
      }
      ${TARGET_FIELDS}
    `,
    args
  );

/** Lo que la detección de cartas cree ver en la mesa al aire; null = nada reciente. */
export const getStreamCardSuggestions = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{ data: StreamCardSuggestions | null }>(
    gql`
      query streamCardSuggestions($tournamentId: Int!) {
        data: streamCardSuggestions(tournamentId: $tournamentId) {
          sessionId
          matchId
          updatedAt
          cards {
            konamiId
            name
            imageUrl
            confidence
            firstSeenAt
            lastSeenAt
          }
        }
      }
    `,
    { tournamentId }
  );

// =============================================================================
// REST
// =============================================================================

/** Sube la imagen de un override del panel y devuelve su URL (<= 200 chars). */
export const uploadStreamControlImage = async (
  tournamentId: number,
  file: File
): Promise<string> => {
  const formData = new FormData();
  formData.append('image', file);

  const { data } = await axiosClient.post<{ imageUrl: string }>(
    `/streaming/control/${tournamentId}/images`,
    formData,
    { headers: { 'Content-Type': undefined } }
  );
  return data.imageUrl;
};
